import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/layout/header';
import { FlowBuilder } from '@/components/flow-builder/flow-builder';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import { useAppStore } from '@/stores/app-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useAppInit } from '@/hooks/use-app-init';
import { useTheme } from '@/hooks/use-theme';
import { TwilioSinkAdminGuard } from '@/components/settings/twilio-sink-admin-guard';
import { EventWebhookAdminGuard } from '@/components/settings/event-webhook-admin-guard';
import type { MMMCard } from '@/types/field-mapping-flow';

/**
 * Shared field mapping flow content — renders the flow builder with its own
 * sub-header (back button, save/discard). Used by both AppLayout (when
 * actionType=fieldMapping) and the standalone FieldMappingFlowPage.
 */
export function FieldMappingFlowContent() {
  const { ready, initError } = useAppInit();
  const extensionName = useAppStore((s) => s.extensionName);
  const {
    loading,
    dirtyBranches,
    fetchFieldMappings,
    fetchRecentEvents,
    fetchEventTypeBranches,
    subscribeToRecentEvents,
    discardChanges,
    reset,
  } = useFlowBuilderStore();

  // Load field mapping data once app init is ready
  useEffect(() => {
    if (!ready || initError) return;

    const loadData = async () => {
      try {
        // 0. Load incoming capture settings (contains ignoredEventTypes for
        //    filtering disabled branches + targetModule for Default Actions node)
        //    AND field mappings in parallel.
        //    Settings must be ready BEFORE setLoading(false) so the canvas
        //    renders with disabled branches already hidden (no flash).
        const [, , ,] = await Promise.all([
          useSettingsStore.getState().loadEventIncomingCaptureSettings(),
          useSettingsStore.getState().loadIncomingCaptureSettings(),
          fetchFieldMappings(),
        ]);
        // 2. Fetch event type branches from TPA API (events apps) — must run
        //    after fetchFieldMappings so it can detect existing branches.
        await fetchEventTypeBranches();
      } finally {
        // 3. All branches + ignoredEventTypes ready — hide loading
        useFlowBuilderStore.getState().setLoading(false);
      }
      // 4. Fetch recent events once, then subscribe for real-time updates
      await fetchRecentEvents();
      subscribeToRecentEvents();
    };

    loadData();

    return () => {
      reset(); // reset() also calls unsubscribeRecentEvents()
    };
  }, [ready, initError, fetchFieldMappings, fetchRecentEvents, fetchEventTypeBranches, subscribeToRecentEvents, reset]);

  const hasDirtyBranches = dirtyBranches.size > 0;
  const [savingAll, setSavingAll] = useState(false);

  const handleSaveAll = async () => {
    // ── Create-before-Update guard ──
    // For MMM branches, ensure no card has update fields without create fields configured.
    const { fieldMappings, dirtyBranches: dirtySet } = useFlowBuilderStore.getState();
    const branchIds = Array.from(dirtySet);

    for (const docId of branchIds) {
      const mapping = fieldMappings[docId];
      if (!mapping?.isMMM) continue;

      const fields = mapping.fields || {};
      for (const key of Object.keys(fields)) {
        const card = fields[key];
        if (typeof card !== 'object' || card === null || !('cardId' in card)) continue;
        const mmmCard = card as MMMCard;

        // Skip cards that always create new (no update mapping expected)
        if (mmmCard.syncToExisting === false) continue;

        const createRegularCount = Object.keys(mmmCard.fields || {})
          .filter((k) => !k.startsWith('UA_INCOMING_')).length;
        const updateRegularCount = Object.keys(mmmCard.fieldsForExisting?.fields || {})
          .filter((k) => !k.startsWith('UA_INCOMING_')).length;

        if (updateRegularCount > 0 && createRegularCount === 0) {
          toast.error(
            `Please configure the "Create New Record" mapping for "${mmmCard.displayLabel || 'a module'}" before saving update mapping.`,
          );
          return; // Abort entire Save All
        }
      }
    }

    setSavingAll(true);
    try {
      // Save branches sequentially to avoid race conditions when multiple
      // new_ branches are saved concurrently (each save replaces temp IDs
      // in the shared fieldMappings state).
      for (const docId of branchIds) {
        await useFlowBuilderStore.getState().saveBranch(docId);
      }
      toast.success('All changes saved');
    } catch (error) {
      console.error('[FlowBuilder] Save all failed:', error);
      toast.error('Failed to save some changes');
    } finally {
      setSavingAll(false);
    }
  };

  // Handle initialization error
  if (initError) {
    return (
      <div className="flex flex-1 items-center justify-center bg-background">
        <div className="max-w-md text-center space-y-4 p-8">
          <h2 className="text-xl font-semibold text-destructive">
            Initialization Error
          </h2>
          <p className="text-sm text-muted-foreground">{initError}</p>
          <p className="text-xs text-muted-foreground">
            Make sure you have a valid appCode in the URL, e.g.,{' '}
            <code className="bg-muted px-1 py-0.5 rounded">
              ?appCode=twilio-zohocrm
            </code>
          </p>
        </div>
      </div>
    );
  }

  return (
    <TwilioSinkAdminGuard variant="fullscreen">
      <EventWebhookAdminGuard>
      {/* App top bar */}
      <Header hideNav />

      {/* Page header */}
      <header
        data-testid="field-mapping-flow"
        className="flex h-12 shrink-0 items-center justify-between border-b border-border bg-background px-4"
      >
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-sm font-semibold">Field Mapping Flow</h1>
            {extensionName && (
              <p className="text-xs text-muted-foreground">{extensionName}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasDirtyBranches && (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {dirtyBranches.size} unsaved {dirtyBranches.size === 1 ? 'change' : 'changes'}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={discardChanges}
              >
                Discard
              </Button>
            </>
          )}
          <Button
            size="sm"
            onClick={handleSaveAll}
            disabled={!hasDirtyBranches || loading || savingAll}
          >
            {savingAll ? 'Saving…' : 'Save All'}
          </Button>
        </div>
      </header>

      {/* Flow Builder */}
      <div className="flex-1 overflow-hidden">
        <FlowBuilder />
      </div>
      </EventWebhookAdminGuard>
    </TwilioSinkAdminGuard>
  );
}

/**
 * Standalone page wrapper — used when field mapping is accessed via a
 * standalone route (e.g. /field-mapping). Provides its own full-screen
 * container, theme, and app init.
 */
export function FieldMappingFlowPage() {
  useAppInit();
  useTheme();

  return (
    <div className="flex h-screen w-screen flex-col bg-background">
      <FieldMappingFlowContent />
    </div>
  );
}
